<script lang="ts" setup>
import {useRouter} from 'vue-router';
import {useSessionStore, useDashboardStore} from 'src/store';
import {useI18n} from 'vue-i18n';
import {
  computed,
  onMounted,
  ref
} from 'vue';
import Box from 'src/components/BoxContainer.vue';

const router = useRouter(),
  sessionStore = useSessionStore(),
  dashboardStore = useDashboardStore(),
  { t } = useI18n();

// Component options
const isLoading = ref(true);

// Computed properties
const firstName = computed(() => sessionStore.user?.firstName),
  sessionProcedures = computed(() => sessionStore.procedures || []);

const currentProcedure = computed(() => sessionProcedures.value.find(proc => proc.eocId === sessionStore.user?.selectedEocId));

const firstTask = computed(() => dashboardStore?.tasks?.[0]);

const surveyRoute = computed(() => {
  const task = firstTask.value;
  if (!task) return null;
  return {
    name: 'Survey',
    params: {
      surveyId: task.id,
      tickSignature: task.tickSignature,
    },
  };
});

onMounted(async () => {
  if (currentProcedure.value == null) {
    await router.push({ name: 'SelectCarePlan' });
  }
  else if (!firstTask.value)
  { 
    await router.push({ name: 'Dashboard.MLHome' });
  }
  isLoading.value = false;
});
</script>

<template>
  <q-page class="not-auth-header" padding>
    <div class="row bs-gutter justify-center">

      <!-- Box -->
      <Box :box-bkgd-color="$zb.enums.BoxBackgroundColorEnum.linearGradient"
        :box-brdr-radius="$zb.enums.BoxBorderRadiusEnum.lg" :box-drop-shadow="$zb.enums.BoxDropShadowEnum.xl"
        :box-padding="$zb.enums.BoxPaddingEnum.p505065" :box-width="$zb.enums.BoxWidthEnum.md" class="col-auto">

        <!-- Hand Wave Icon -->
        <zbm-skeleton-loader class="mb-20" height="100px" width="50%" :loading="isLoading">
          <div class="row justify-center">
            <div class="hand-wave-icon">
              <q-icon name="fa-thin fa-hand-wave" size="75px" />
            </div>
          </div>
        </zbm-skeleton-loader>

        <!-- Heading -->
        <div class="row heading">
          <div class="col text-center mb-50">
            <h1 class="text-content-grd-bg-content q-mb-lg">
              <zbm-skeleton-loader height="34px" :loading="isLoading" width="100%">
                {{ t('vue3_eoc_selection_header', [firstName]) }}
              </zbm-skeleton-loader>
            </h1>
            <p class="text-content-grd-bg-content font-size-18">
              <zbm-skeleton-loader height="30px" :loading="isLoading" width="100%">
                {{ t('prom_auto_launch_intro', [currentProcedure?.surgeonLastName, currentProcedure?.lateralityBodyPartLocalized]) }}
              </zbm-skeleton-loader>
            </p>
          </div>
        </div>

        <div class="row footer">
          <div class="col text-center">
            <zbm-btn :to="surveyRoute" :color="$zb.enums.ButtonStdColorEnum.darkTeal"
              :height="$zb.enums.ButtonHeightEnum.lg" :label="t('get_started')" data-testid="getStartedButton" />
          </div>
        </div>
      </Box>
    </div>
  </q-page>
</template>

<style scoped lang="scss">
.hand-wave-icon {
  width: 200px;
  height: 200px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 25px;
    left: 25px;
    right: 25px;
    bottom: 25px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.1);
  }
  
  .q-icon {
    color: white;
    font-weight:100;
    z-index: 1;
  }
}
</style>
